import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTable, usePagination, useSortBy } from "react-table";
import headerDefinitions from "../tableheaders/learnerclasssection.tableheader.json";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import helpers from "../helpers/function.helper";
import LoadingComponent from "../components/Loading";
import { getUsersList } from "../api/users.api";
import Swal from "sweetalert2";
import viewIcon from "../assets/icons/view.png";
import certIcon from "../assets/icons/cert.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import TableHeaderButton from "../components/TableHeaderButton";
import "./LearnerClassSection.css";
import SidePanelContent from "../components/SidePanelContent";

const LearnerClassSection = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [isLoading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);

  const progressData = [
    {
      title: "ประสิทธิภาพของรถ BEV เทียบกับรถน้ำมัน 01",
      subsections: [
        { name: "ก่อนเรียน", progress: 75 },
        { name: "เนื้อหา", progress: 100 },
        { name: "หลังเรียน", progress: 90 },
        { name: "การบ้าน", progress: 90 },
      ],
    },
    {
      title: "ประสิทธิภาพของรถ BEV เทียบกับรถน้ำมัน 02",
      subsections: [
        { name: "ก่อนเรียน", progress: 76 },
        { name: "เนื้อหา", progress: 13 },
        { name: "หลังเรียน", progress: 55 },
        { name: "การบ้าน", progress: 41 },
      ],
    },
    // Add more sections as needed
  ];

  const handleViewClick = async (section) => {
    setSelectedSection(section);
    setShowSidePanel(true);
  };

  const handleClose = () => {
    setShowSidePanel(false);
    setSelectedSection(null);
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.keyCode === 27) handleClose();
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleClose]);

  const handleClickOutside = useCallback(
    (event) => {
      if (!event.target.closest(".side-panel")) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // -------------------- Data Table ----------------------------------------
  const headersData = headerDefinitions.columns;
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataTable, setDatatable] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [canNextPage, setCanNextPage] = useState(true);
  const [canPreviousPage, setCanPreviousPage] = useState(false);
  const tableData = useMemo(() => dataTable, [dataTable]);
  const columns = useMemo(() => headersData, [headersData]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: tableData,
      },
      useSortBy,
      usePagination
    );

  const toggleRowSelection = (Id) => {
    setSelectedRowIds((prev) => {
      if (prev.includes(Id)) {
        // If already selected, remove it
        return prev.filter((id) => id !== Id);
      } else {
        // Otherwise, add it
        return [...prev, Id];
      }
    });
  };

  const handleSort = async (columnId) => {
    // Will Sort By *accessor* in HeaderJSON File
    setSortConfig((prevSortConfig) => {
      let direction = "ASC";
      if (
        prevSortConfig.key === columnId &&
        prevSortConfig.direction === "ASC"
      ) {
        direction = "DESC";
      }
      return { key: columnId, direction };
    });
  };

  const handleNextPage = () => {
    setPageIndex((current) => {
      const nextPageIndex = current + 1;
      setCanNextPage(nextPageIndex < pageCount - 1);
      setCanPreviousPage(true);
      return nextPageIndex;
    });
  };

  const handlePreviousPage = () => {
    setPageIndex((current) => {
      const previousPageIndex = Math.max(0, current - 1);
      setCanPreviousPage(previousPageIndex > 0);
      setCanNextPage(true);
      return previousPageIndex;
    });
  };

  // -------------------- Data Table ----------------------------------------

  const clearSearch = () => {
    setKeyword("");
    setSortConfig({ key: "", direction: "" });
    setRowsPerPage(10);
    setPageIndex(0);
  };

  const fetchData = async (is_search = false) => {
    try {
      setLoading(true);
      const { data, totalPages } = await getUsersList({
        page_number: is_search ? 0 : pageIndex,
        data_per_page: is_search ? 10 : rowsPerPage,
        keyword: keyword,
        sort_key: sortConfig.key,
        sort_order: sortConfig.direction,
      });
      if (data) {
        setDatatable(data);
        setPageCount(totalPages);
        setCanNextPage((is_search ? 0 : pageIndex) < Math.ceil(totalPages / (is_search ? 10 : rowsPerPage)) - 1);
        setCanPreviousPage((is_search ? 0 : pageIndex) > 0);
      }
    } catch (error) {
      console.error("Fetch Data Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    const swalWithBootstrapButtons = Swal.mixin({}); // Providing an empty object as argument

    let res = await swalWithBootstrapButtons.fire({
      title: "คุณต้องการที่ดาวน์โหลด ใช่หรือไม่",
      icon: "warning",
      confirmButtonText: "ตกลง",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
      showCancelButton: true,
    });
    if (res.isConfirmed) {
      try {
        setLoading(true);
        window.open(
          `${process.env.REACT_APP_BASE_URL}/institution/export`,
          "_blank"
        );
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // fetchData();
  }, []);

  return (
    <div className="content pb-5">
      <div className="container-fluid">
        <div className="box-white">
          {/* <h4>ประวัติแลกของรางวัล</h4> */}

          <div className="d-flex justify-content-between align-items-center">
            <div className="stat-box lessons class-info-section d-flex flex-column p-3">
              <h5>รหัสห้องเรียน C001</h5>
              <h4>
                ชื่อห้องเรียน{" "}
                <span className="text-primary">รถไฟฟ้า EV101</span>
              </h4>
              <div className="d-flex align-items-center mt-2">
                <span className="mx-2">
                  สถานะ: <span className="text-success">กำลังเรียน</span>
                </span>
                |{/* <div className="vertical-divider mx-2"></div> */}
                <span className="mx-2">
                  ระยะเวลา 1 ส.ค. 2567 ถึง 31 ส.ค. 2567
                </span>
                |{/* <div className="vertical-divider mx-2"></div> */}
                <span className="mx-2">ผู้สอน TEA-001 สิริชัย อัครวัฒน์</span>
              </div>
            </div>
            <div className="class-stats d-flex">
              <div className="stat-box lessons bg-purple text-center p-3 mx-2">
                <p>จำนวนบทเรียน</p>
                <h3 className="text-primary">10</h3>
              </div>
              <div className="stat-box score bg-green text-center p-3 mx-2">
                <p>คะแนนเก็บ</p>
                <h3 className="text-success">50</h3>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mx-2 py-3">
            <div className='d-none d-md-block'>
              <span className="pr-2 align-self-center text-sm fw-bold">
                Show :{" "}
              </span>
              <label>
                <select
                  style={{ border: "none", cursor: "pointer" }}
                  className="select-page mr-2"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
              <label>Entries</label>
            </div>
            <div className="d-flex search-container">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="ค้นหา"
                className="search-input"
              />
              <button className="search-button" onClick={() => fetchData(true)}>
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>
          </div>
          <div style={{ overflow: "auto" }}>
            {" "}
            <Table
              striped
              bordered
              {...getTableProps()}
              className="table text-center"
              style={{ borderCollapse: "collapse" }}
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr key={"head"} {...headerGroup.getHeaderGroupProps()}>
                    {/* <th>ลำดับ</th> */}
                    {headerGroup.headers.map((column) => {
                      const { key, ...rest } = column.getHeaderProps(); // Separate key from other props
                      return (
                        <th key={key} {...rest}>
                          {column.render("Header")}
                          {/* Add a clickable sort icon */}
                          <TableHeaderButton
                            column={column}
                            sortConfig={sortConfig}
                            handleSort={handleSort}
                          />
                        </th>
                      );
                    })}
                    <th>เครื่องมือ</th>
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                <tr className="space-table"></tr>
                {/* MOCK */}
                <tr>
                  <td>1</td>
                  <td>บทเรียน 1.1 Steering Wheel Tube</td>
                  <td>10</td>
                  <td>กำลังเรียน</td>
                  <td>
                    <button
                      className="icon-btn"
                      style={{ background: "transparent" }}
                      onClick={() => handleViewClick({ cls_name: "ทดสอบ" })}
                    >
                      <img src={viewIcon} alt="View" className="ic-actions" />
                    </button>
                  </td>
                </tr>
                {/* MOCK */}

                {rows.length > 0 ? (
                  rows.map((row, rowIndex) => {
                    prepareRow(row);
                    const actualIndex = pageIndex * rowsPerPage + rowIndex + 1;
                    const isSelected = selectedRowIds.includes(row.original.id);

                    // Extract key from row.getRowProps()
                    const { key, ...rest } = row.getRowProps();

                    return (
                      <tr
                        key={key} // Pass key directly here
                        {...rest}
                        onClick={() => toggleRowSelection(row.original.id)}
                        className={isSelected ? "selected-row" : ""}
                        style={isSelected ? { backgroundColor: "#f0f0f0" } : {}}
                      >
                        <td>{actualIndex}</td>
                        <td>{row.original?.ins_code ?? ""}</td>
                        <td>{row.original?.ins_name ?? ""}</td>
                        <td>
                          <span
                            style={{
                              color: row.original.is_active
                                ? "#87BD66"
                                : "#D95350",
                            }}
                          >
                            {helpers.getStatus(row.original.is_active)}
                          </span>
                        </td>
                        <td>
                          <button
                            className="mx-1 icon-btn"
                            style={{ background: "transparent" }}
                          >
                            <img
                              src={viewIcon}
                              alt="View"
                              className="ic-actions"
                            />
                          </button>

                          <button
                            className="mx-1 icon-btn"
                            style={{ background: "transparent" }}
                          >
                            <img
                              src={certIcon}
                              alt="Edit"
                              className="ic-actions"
                            />
                          </button>

                          {/* <button
                      className="mx-1 icon-btn"
                      style={{ background: 'transparent' }}
                      onClick={(e) => { navigate(``); }}
                    >
                      <img src={deleteIcon} alt="Delete" className="ic-actions" />
                    </button> */}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="text-center">
                    <td colSpan={100} style={{ textAlignLast: "center" }}>
                      ไม่พบข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div
            className="py-3 mx-2"
            style={{ display: "flex", justifyContent: "right" }}
          >
            {/* Pagination */}
            <div className="mt-2">
              <span>
                {`${pageIndex * rowsPerPage + 1} - ${
                  (pageIndex + 1) * rowsPerPage
                } จาก ${pageCount} `}
              </span>
              <button
                className="mx-1 px-3 icon-btn"
                onClick={handlePreviousPage}
                disabled={!canPreviousPage}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <span> {pageIndex + 1}</span>
              <button
                className="mx-1 px-3 icon-btn"
                onClick={handleNextPage}
                disabled={!canNextPage}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <span>{Math.ceil(pageCount / rowsPerPage)}</span>
            </div>
          </div>

          {/* Side Panel */}
          {selectedSection && (
            <div
              className={`side-panel ${showSidePanel ? "open" : ""}`}
              style={{ width: "70%" }}
            >
              <div className="learner-side-panel-header d-flex justify-content-between align-items-center">
                <div>
                  <h4>ความคืบหน้าบทเรียน I รถไฟฟ้า EV101</h4>
                  <h5 className="mt-1">
                    THA0001{" "}
                    <span className="text-header">สิริชัย อัครวัฒน์</span>
                  </h5>
                </div>
                <div className="d-flex align-items-center">
                  <p className="total-progress mb-0">
                    <span className="fs-0 fw-light mx-1">รวมทั้งหมด</span>
                    <span className="text-header fs-1 fw-bold mx-1">50%</span>
                  </p>
                  <Button
                    variant="link"
                    onClick={handleClose}
                    className="learner-close-btn ml-3"
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                </div>
              </div>
              <SidePanelContent progressData={progressData} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearnerClassSection;
